// @react-compiler enable
import {testIdProps} from '@github-ui/test-id-props'
import {clsx} from 'clsx'
import {memo} from 'react'

import type {TitleValueWithContentType} from '../../../api/columns/contracts/title'
import {useTableHierarchy} from '../../../features/sub-issues/use-table-hierarchy'
import {isNumber, parseTitleNumber} from '../../../helpers/parsing'
import {useTableSidePanel} from '../../../hooks/use-side-panel'
import {type ColumnValue, hasValue} from '../../../models/column-value'
import type {MemexItemModel} from '../../../models/memex-item-model'
import {MemexItemIcon} from '../../common/memex-item-icon'
import {TextPlaceholder} from '../../common/placeholders'
import {InteractiveItemTitle} from '../../interactive-item-title'
import {BaseCell} from '../cells/base-cell'
import {ExpandButton, type ExpandButtonProps} from '../expand-button'
import {useRecordCellRenderer} from '../performance-measurements'
import styles from './title-renderer.module.css'

type Props = Readonly<
  {
    currentValue: ColumnValue<TitleValueWithContentType>
    model: MemexItemModel
    isDisabled?: boolean
    rowId: string
    /** If defined, represents the number of levels deep this item is in a sub-issues hierarchy. */
    subIssueDepth?: number
  } & Omit<ExpandButtonProps, 'issueId'>
>

export const LoadingTitleCell = () => (
  <BaseCell>
    <TextPlaceholder minWidth={80} maxWidth={200} {...testIdProps('placeholder')} />
  </BaseCell>
)

export const TitleRenderer = memo(function TitleRenderer({
  currentValue,
  model,
  isDisabled,
  subIssueDepth = 0,
  expandable,
  expanded,
  rowId,
  expandRef,
}: Props) {
  useRecordCellRenderer('TitleRenderer', model.id)
  const titleValue = hasValue(currentValue) ? currentValue.value : undefined
  const number = parseTitleNumber(titleValue)
  const {openPane} = useTableSidePanel()
  const {hierarchyEnabled} = useTableHierarchy()
  const issueId = model.contentType === 'Issue' ? model.content.id.toString() : undefined

  return (
    <BaseCell>
      {hierarchyEnabled && (
        <div
          style={{'--hierarchy-depth': subIssueDepth, '--expandable': expandable ? 1 : 0}}
          className={styles.hierarchyDepth}
        >
          <ExpandButton
            expandRef={expandRef}
            expanded={expanded}
            expandable={expandable}
            issueId={issueId}
            nwoReference={model.getNameWithOwnerReference()}
            rowId={rowId}
          />
        </div>
      )}
      <div className={styles.Box}>
        <MemexItemIcon titleColumn={currentValue} isBlocked={model.isBlocked()} />
      </div>
      <InteractiveItemTitle model={model} isDisabled={isDisabled} currentValue={currentValue} openPane={openPane} />
      {isNumber(number) && (
        <div className={styles.Box_1}>
          <span className={styles.Text}> #{number}</span>
        </div>
      )}
    </BaseCell>
  )
})

export const LoadingSubIssuesCell = (subIssueDepth: number) => (
  <BaseCell style={{'--hierarchy-depth': subIssueDepth, '--missing': 1}} className={styles.hierarchyDepth}>
    <TextPlaceholder minWidth={80} maxWidth={200} {...testIdProps('placeholder')} />
  </BaseCell>
)

export const MissingSubIssuesRenderer = ({
  missingSubIssues,
  subIssueDepth,
}: {
  missingSubIssues: number
  subIssueDepth: number
}) => {
  const missingSubIssuesText = `${missingSubIssues} sub-issue${missingSubIssues !== 1 ? 's' : ''} not visible`
  return (
    <BaseCell
      style={{'--hierarchy-depth': subIssueDepth, '--missing': 1}}
      className={clsx(styles.missingSubIssuesText, styles.hierarchyDepth)}
    >
      {missingSubIssuesText}
    </BaseCell>
  )
}

try{ LoadingTitleCell.displayName ||= 'LoadingTitleCell' } catch {}
try{ TitleRenderer.displayName ||= 'TitleRenderer' } catch {}
try{ LoadingSubIssuesCell.displayName ||= 'LoadingSubIssuesCell' } catch {}
try{ MissingSubIssuesRenderer.displayName ||= 'MissingSubIssuesRenderer' } catch {}